/* eslint-disable tailwindcss/no-arbitrary-value */
import React from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { Typography } from '@one-thd/sui-atomic-components';
import { ProductImage } from '@thd-olt-component-react/product-pod';

const MAX_THUMBNAILS = 5;
const TILE_SIZE = 70;

// An unresolved intent has no itemId and so no image to show, but it still occupies a
// slot: the strip previews everything being added, and skipping intents would
// under-report the selection.
const isIntentItem = (item) => item?.itemType === 'intent' || !item?.itemId;

/**
 * A row of item icons previewing a multi-item selection: an image per resolved product, a
 * plus separator after the first, a neutral placeholder for each unresolved intent, and a
 * trailing +N tile once the row overflows.
 */
export const ItemIconStrip = ({ items, className }) => {
  if (!items?.length) return null;

  return (
    <div className={`sui-flex sui-gap-2 sui-items-center ${className}`} data-testid="item-icon-strip">
      {items.slice(0, MAX_THUMBNAILS).map((item, index) => {
        const isLastSlot = index === MAX_THUMBNAILS - 1 && items.length > MAX_THUMBNAILS;
        const key = item.itemId || `intent-${index}`;
        const plusSeparator = index === 1 ? (
          <div key={`plus-${index}`} className="sui-px-2px">
            <Typography variant="body-lg" weight="bold" height="tight">+</Typography>
          </div>
        ) : null;

        let thumbnail;
        if (isLastSlot) {
          thumbnail = (
            <div
              key={key}
              className="sui-flex-shrink-0 sui-bg-strong sui-aspect-square sui-grid sui-place-items-center"
              style={{ width: TILE_SIZE, height: TILE_SIZE }}
              data-testid="item-icon-overflow"
            >
              <Typography variant="body-base" weight="bold">
                +{items.length - MAX_THUMBNAILS + 1}
              </Typography>
            </div>
          );
        } else if (isIntentItem(item)) {
          thumbnail = (
            <div
              key={key}
              className="sui-flex-shrink-0 sui-bg-strong sui-aspect-square"
              style={{ width: TILE_SIZE, height: TILE_SIZE }}
              data-testid="item-icon-intent-placeholder"
              aria-label="Item to be chosen later"
              role="img"
            />
          );
        } else {
          thumbnail = (
            <div
              key={key}
              className="sui-flex-shrink-0 sui-overflow-hidden"
              style={{ width: TILE_SIZE, height: TILE_SIZE }}
            >
              <ProductImage itemId={item.itemId} />
            </div>
          );
        }

        return [plusSeparator, thumbnail];
      })}
    </div>
  );
};

ItemIconStrip.displayName = 'ItemIconStrip';

ItemIconStrip.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    itemType: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  className: PropTypes.string
};

ItemIconStrip.defaultProps = {
  items: [],
  className: ''
};

ItemIconStrip.dataModel = extend(ProductImage);
